import React from 'react';
import { useAuthStore } from '@/store/auth.store';
import { ROLE_CONFIG, ROLE_TO_NAV_KEY } from '@/types/auth.types';
import { I } from '@/icons';

/** Navigation items per authenticated role key. */
const NAV_BY_ROLE: Record<
  string,
  Array<{ section?: string; id?: string; label?: string; icon?: keyof typeof I }>
> = {
  coord: [
    { section: 'Operations' },
    { id: 'intake', label: 'Intake queue', icon: 'inbox' },
    { id: 'audit', label: 'Audit trail', icon: 'audit' },
    { section: 'Configure' },
    { id: 'rules', label: 'Rules & checklists', icon: 'rules' },
    { id: 'setup', label: 'Word add-in setup', icon: 'word' },
  ],
  spec: [
    { section: 'My work' },
    { id: 'intake', label: 'Job queue', icon: 'inbox' },
    { id: 'workspace', label: 'Active document', icon: 'edit' },
    { section: 'Reference' },
    { id: 'rules', label: 'Rules library', icon: 'rules' },
    { id: 'setup', label: 'Word add-in setup', icon: 'word' },
  ],
  qc: [
    { section: 'Review' },
    { id: 'intake', label: 'QC queue', icon: 'inbox' },
    { id: 'qc', label: 'Checklist runner', icon: 'check' },
    { id: 'workspace', label: 'AI suggestions', icon: 'edit' },
    { id: 'proofread', label: 'Proofread', icon: 'read' },
    { section: 'Compliance' },
    { id: 'audit', label: 'Audit trail', icon: 'audit' },
    { id: 'setup', label: 'Word add-in setup', icon: 'word' },
  ],
  proof: [
    { section: 'My work' },
    { id: 'intake', label: 'Proof Reader Queue', icon: 'inbox' },
    { id: 'workspace', label: 'Review document', icon: 'read' },
    { section: 'Reference' },
    { id: 'setup', label: 'Word add-in setup', icon: 'word' },
  ],
};

interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
}

export function Sidebar({ currentPage, onNavigate }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const roleKey = user ? ROLE_TO_NAV_KEY[user.role] : 'coord';
  const nav = NAV_BY_ROLE[roleKey] || NAV_BY_ROLE.coord;
  const roleConfig = user ? ROLE_CONFIG[user.role] : { label: 'Coordinator', shortLabel: 'Coordinator' };
  const userName = user?.name || 'Guest';
  const userInitials = userName
    .split(' ')
    .map((w) => w[0])
    .join('');

  return (
    <aside className="sidebar">
      {/* Brand */}
      <div className="brand">
        <img
          src="/klara-logo.png"
          alt="Klara"
          style={{
            width: 32,
            height: 32,
            objectFit: "contain",
            borderRadius: 6,
          }}
        />
        <div>
          <div className="brand-name">Klara</div>
          <div className="brand-sub">GRSS · AI QA/QC</div>
        </div>
      </div>

      {/* Role indicator */}
      <div
        style={{
          margin: 12,
          padding: '10px 12px',
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--accent)',
          }}
        />
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>
          {roleConfig.shortLabel}
        </span>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, overflow: 'auto', paddingBottom: 8 }}>
        {nav.map((item, i) => {
          if (item.section) {
            return (
              <div key={i} className="nav-section">
                {item.section}
              </div>
            );
          }
          const IconComp = item.icon ? I[item.icon] : null;
          return (
            <div key={item.id} className="nav-list">
              <button
                className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
                onClick={() => onNavigate(item.id!)}
              >
                {IconComp && React.createElement(IconComp)}
                <span>{item.label}</span>
                {currentPage === item.id && <span className="nav-dot" />}
              </button>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <div className="avatar">{userInitials}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {userName}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            {roleConfig.label} · GRSS Phoenix
          </div>
        </div>
        <button
          onClick={logout}
          className="btn ghost sm"
          style={{
            padding: 6,
            color: 'var(--muted)',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
          }}
          title="Log out"
        >
          <I.logOut />
        </button>
      </div>
    </aside>
  );
}
